import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Store, BookOpen } from "lucide-react";

const CATEGORY_TAGS = [
  { value: "grocery", label: "Grocery" },
  { value: "meat", label: "Meat & Dairy" },
  { value: "condiments", label: "Condiments" },
  { value: "beverages", label: "Beverages" },
  { value: "other", label: "Other" },
];

const EMPTY = { name: "", category_tag: "grocery", phone: "", email: "", address: "", gstin: "", payment_terms: "", notes: "" };

import VendorLedger from "./VendorLedger";

export default function VendorsTab({ onViewLedger }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // vendor object OR "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [ledgerVendorId, setLedgerVendorId] = useState(null);

  const openLedger = (id) => {
    if (onViewLedger) onViewLedger(id);
    else setLedgerVendorId(id);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/vendors");
      setItems(data);
    } catch {
      toast.error("Could not load vendors");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (v) => { setEditing(v); setForm({ ...EMPTY, ...v }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Vendor name is required"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        const { data } = await api.post("/staff/vendors", form);
        setItems((p) => [...p, data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`Added ${data.name}`);
      } else {
        const { data } = await api.patch(`/staff/vendors/${editing.id}`, form);
        setItems((p) => p.map((v) => (v.id === data.id ? data : v)));
        toast.success(`Updated ${data.name}`);
      }
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Deactivate vendor "${v.name}"? Past POs remain intact.`)) return;
    try {
      await api.delete(`/staff/vendors/${v.id}`);
      setItems((p) => p.map((x) => (x.id === v.id ? { ...x, is_active: false } : x)));
      toast.success("Deactivated");
    } catch { toast.error("Delete failed"); }
  };

  const filtered = filter === "all"
    ? items
    : filter === "inactive"
      ? items.filter((v) => !v.is_active)
      : items.filter((v) => v.is_active && v.category_tag === filter);

  if (ledgerVendorId) {
    return <VendorLedger vendorId={ledgerVendorId} onBack={() => setLedgerVendorId(null)} />;
  }

  return (
    <div data-testid="vendors-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex flex-wrap gap-1 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] rounded-full p-1">
          {["all", ...CATEGORY_TAGS.map((c) => c.value), "inactive"].map((f) => (
            <button
              key={f}
              data-testid={`vendor-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-[11px] uppercase tracking-wider rounded-full transition-all ${
                filter === f ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"
              }`}
            >
              {f === "all" ? "All" : f === "inactive" ? "Inactive" : CATEGORY_TAGS.find((c) => c.value === f)?.label || f}
            </button>
          ))}
        </div>
        <button data-testid="vendor-new" onClick={openNew} className="btn-brass !py-2 !px-4 text-sm">
          <Plus size={14} /> New vendor
        </button>
      </div>

      <div className="mt-6 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
        {loading && <div className="p-6 text-[hsl(var(--ink-soft))]">Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div data-testid="vendors-empty" className="p-10 text-center text-[hsl(var(--ink-soft))]">
            <Store className="mx-auto mb-2 opacity-40" size={28} />
            No vendors yet — add your first one.
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <ul className="divide-y divide-[hsl(var(--rule))]">
            {filtered.map((v) => (
              <li key={v.id} data-testid={`vendor-${v.id}`} className="p-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
                <div>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="text-base font-semibold">{v.name}</div>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[hsl(var(--brass)/0.15)] text-[hsl(var(--brass-2))]">
                      {CATEGORY_TAGS.find((c) => c.value === v.category_tag)?.label || v.category_tag}
                    </span>
                    {!v.is_active && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]">Inactive</span>}
                  </div>
                  <div className="mt-1 text-sm text-[hsl(var(--ink-soft))]">
                    {v.phone && <span>{v.phone}</span>}
                    {v.email && <> · {v.email}</>}
                    {v.gstin && <> · GSTIN: <span className="font-mono">{v.gstin}</span></>}
                  </div>
                  {v.address && <div className="mt-0.5 text-xs text-[hsl(var(--ink-soft))]">{v.address}</div>}
                  {v.payment_terms && <div className="mt-0.5 text-xs text-[hsl(var(--brass-2))]">{v.payment_terms}</div>}
                </div>
                <div className="flex gap-2">
                  <button data-testid={`vendor-ledger-${v.id}`} onClick={() => openLedger(v.id)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--forest))] text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors">
                    <BookOpen size={12} /> Ledger
                  </button>
                  <button data-testid={`vendor-edit-${v.id}`} onClick={() => openEdit(v)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg))]">
                    <Pencil size={12} /> Edit
                  </button>
                  {v.is_active && (
                    <button data-testid={`vendor-del-${v.id}`} onClick={() => remove(v)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]">
                      <Trash2 size={12} /> Deactivate
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <div data-testid="vendor-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
          <form onSubmit={save} className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-xl p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="eyebrow">{editing === "new" ? "New vendor" : "Edit vendor"}</div>
                <h2 className="mt-1 text-2xl">{form.name || "—"}</h2>
              </div>
              <button type="button" onClick={close} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Name *
                <input required data-testid="vendor-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="tbb-inp mt-1 w-full" autoFocus />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Category
                <select data-testid="vendor-form-category" value={form.category_tag} onChange={(e) => setForm({ ...form, category_tag: e.target.value })} className="tbb-inp mt-1 w-full">
                  {CATEGORY_TAGS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Phone
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="tbb-inp mt-1 w-full" placeholder="+91 …" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Email
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                GSTIN
                <input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="tbb-inp mt-1 w-full font-mono" />
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Address
                <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="tbb-inp mt-1 w-full resize-none" />
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Payment terms
                <input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="tbb-inp mt-1 w-full" placeholder="Net 7 days, on delivery, etc." />
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Notes
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="tbb-inp mt-1 w-full resize-none" />
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={close} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
              <button type="submit" data-testid="vendor-save" disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">{saving ? "Saving..." : "Save vendor"}</button>
            </div>
            <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
          </form>
        </div>
      )}
    </div>
  );
}
